import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import type { CancelSubmittedOrderPayload } from "../../api/clients/portfolio";
import {
  cancelSubmittedOrderApi,
  getHoldingsData,
  getSubmittedOrders,
  getPortfolioSummary,
  getSubmittedOrderById,
  getSchemeFolioHoldingsData,
  getPortfolioSummaryV2,
  getUpcomingOrders,
  getPortfolioCategorySummary,
} from "../../api/clients/portfolio";
import { PortfolioBreakdown, PortfolioSummary } from "../../types";

export const portfolioSummaryQueryOptions = (
  investmentAccountGuid: string,
  enabled?: boolean
) =>
  queryOptions({
    queryKey: [
      "portfolio-summary",
      { investment_account_guid: investmentAccountGuid },
    ],
    queryFn: () => getPortfolioSummary(investmentAccountGuid),
    enabled: !!investmentAccountGuid && enabled,
  });

export const portfolioSummaryV2QueryOptions = (
  investmentAccountGuid: string,
  enabled?: boolean,
  initialData?: PortfolioSummary[] | null
) =>
  queryOptions({
    queryKey: [
      "portfolio-summary-v2",
      { investment_account_guid: investmentAccountGuid },
    ],
    queryFn: () => getPortfolioSummaryV2(investmentAccountGuid),
    enabled: !!investmentAccountGuid && enabled,
    initialData,
  });

export function usePortfolioSummary(
  investmentAccountGuid: string,
  enabled?: boolean
) {
  return useQuery(portfolioSummaryQueryOptions(investmentAccountGuid, enabled));
}

export function usePortfolioSummaryV2(
  investmentAccountGuid: string,
  enabled?: boolean,
  intialData?: PortfolioSummary[] | null
) {
  return useQuery(
    portfolioSummaryV2QueryOptions(investmentAccountGuid, enabled, intialData)
  );
}

export const holdingsQueryOptions = (investmentAccountGuid: string) =>
  queryOptions({
    queryKey: [
      "holdings-data",
      { investment_account_guid: investmentAccountGuid },
    ],
    queryFn: () => getHoldingsData(investmentAccountGuid),
    placeholderData: (data) => data,
    enabled: !!investmentAccountGuid,
  });

export function useHoldingsData(investmentAccountGuid: string) {
  return useQuery(holdingsQueryOptions(investmentAccountGuid));
}

export function useInvalidateSubmittedOrders(investmentAccountId: string) {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["submitted-orders", investmentAccountId],
    });
  }, [queryClient]);
}

export function useCancelSubmittedOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["submitted-orders/cancel"],
    mutationFn: (
      variables: {
        id: string | number;
      } & CancelSubmittedOrderPayload
    ) => {
      const { id, ...payload } = variables;
      return cancelSubmittedOrderApi(id, payload);
    },
    onSuccess: (_data, variables) => {
      const { investment_account_guid, id } = variables;
      queryClient.invalidateQueries({
        queryKey: ["submitted-orders", investment_account_guid],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-submitted-orders", { investment_account_guid }],
      });
      queryClient.invalidateQueries({
        queryKey: ["submited-order-by-id", { id, investment_account_guid }],
      });
    },
  });
}

export const submittedOrdersQueryOptions = (
  variables: {
    investment_account_guid: string;
    page?: number;
    pageSize?: number;
    status?: string;
    orderType?: string;
    from_date?: string;
    to_date?: string;
    productType?: string;
    search?: string;
  },
  options?: { enabled?: boolean }
) =>
  queryOptions({
    ...options,
    queryKey: [
      "submitted-orders",
      variables.investment_account_guid,
      {
        page: variables.page,
        pageSize: variables.pageSize,
        status: variables.status,
        orderType: variables.orderType,
        from_date: variables.from_date,
        to_date: variables.to_date,
        search: variables.search,
        productType: variables.productType,
      },
    ],
    queryFn: () => getSubmittedOrders(variables),
    placeholderData: (data) => data,
    retry: false,
  });

export function useSubmittedOrders(
  variables: {
    investment_account_guid: string;
    page?: number;
    pageSize?: number;
    status?: string;
    orderType?: string;
    productType?: string;
    from_date?: string;
    to_date?: string;
    search?: string;
  },
  options?: { enabled?: boolean }
) {
  return useQuery(submittedOrdersQueryOptions(variables, options));
}

export function useInfiniteSubmittedOrders(
  {
    investmentAccountGuid,
    pageSize,
    fromDate,
    toDate,
    search,
    includeFamilyData,
  }: {
    investmentAccountGuid?: string;
    page: number;
    pageSize: number;
    fromDate?: string;
    toDate?: string;
    search?: string;
    /** Investor-only: every family account's orders instead of one account's. */
    includeFamilyData?: boolean;
  },
  options: { enabled?: boolean } = { enabled: true }
) {
  return useInfiniteQuery({
    queryKey: [
      "infinite-submitted-orders",
      {
        investment_account_guid: investmentAccountGuid,
        search,
        include_family_data: includeFamilyData,
      },
    ],
    queryFn: ({ pageParam = 1 }) =>
      getSubmittedOrders({
        investment_account_guid: investmentAccountGuid,
        page: pageParam,
        pageSize,
        from_date: fromDate,
        to_date: toDate,
        search,
        include_family_data: includeFamilyData,
      }),
    placeholderData: (data) => data,
    retry: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const totalFetchedCount = pages.reduce(
        (total, page) => total + (page.results?.length ?? 0),
        0
      );

      return lastPage.count > totalFetchedCount
        ? (pages.length ?? 0) + 1
        : undefined;
    },
    enabled: options.enabled,
  });
}

export const submitedOrderByIdQueryOptions = (
  variables: {
    id: string | number;
    investment_account_guid: string;
  },
  options?: { enabled?: boolean }
) =>
  queryOptions({
    queryKey: [
      "submited-order-by-id",
      {
        id: variables.id,
        investment_account_guid: variables.investment_account_guid,
      },
    ],
    queryFn: () =>
      getSubmittedOrderById(variables.id, variables.investment_account_guid),
    ...options,
  });

export const useSubmittedOrderById = (
  variables: {
    id: string | number;
    investment_account_guid: string;
  },
  options?: { enabled?: boolean }
) => {
  return useQuery(submitedOrderByIdQueryOptions(variables, options));
};

export const useMultipleSubmittedOrders = (
  variables: {
    ids: (string | number)[];
    investment_account_guid: string;
  },
  options?: { enabled?: boolean }
) => {
  return useQueries({
    queries: variables.ids.map((id) =>
      submitedOrderByIdQueryOptions(
        { id, investment_account_guid: variables.investment_account_guid },
        options
      )
    ),
    combine: (result) => {
      return {
        data: result.map((r) => r.data),
        isLoading: result.some((r) => r.isLoading),
        isError: result.some((r) => r.isError),
      };
    },
  });
};

export const useSchemeFolioHoldingsData = (
  variables: {
    investmentAccountGuid: string;
    empanelled?: boolean;
    search?: string;
  },
  options?: { enabled: boolean }
) => {
  return useQuery({
    queryKey: ["scheme-folio-holdings-data", { ...variables }],
    queryFn: () =>
      getSchemeFolioHoldingsData(
        variables.investmentAccountGuid,
        variables.empanelled,
        variables.search
      ),
    placeholderData: (previousData) => previousData,
    ...options,
  });
};

type UpcomingOrdersVariables = {
  page: number;
  pageSize: number;
  fromDate?: string;
  toDate?: string;
  investmentAccountId?: string;
};

export const upcomingOrdersQueryOptions = (
  {
    page,
    pageSize,
    fromDate,
    toDate,
    investmentAccountId,
  }: UpcomingOrdersVariables,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  queryOptions({
    queryKey: [
      "upcoming-orders",
      { page, pageSize, fromDate, toDate, investmentAccountId },
    ],
    enabled,
    queryFn: () =>
      getUpcomingOrders({
        page,
        pageSize,
        fromDate,
        toDate,
        investmentAccountId,
      }),
  });

export function useUpcomingOrders(
  variables: UpcomingOrdersVariables,
  options?: { enabled?: boolean }
) {
  return useQuery(upcomingOrdersQueryOptions(variables, options));
}

export function useInfiniteUpcomingOrders({
  pageSize,
  fromDate,
  toDate,
  investmentAccountId,
}: {
  page: number;
  pageSize: number;
  fromDate?: string;
  toDate?: string;
  investmentAccountId?: string;
}) {
  return useInfiniteQuery({
    queryKey: ["infinite-upcoming-orders"],
    queryFn: ({ pageParam = 1 }) =>
      getUpcomingOrders({
        page: pageParam,
        pageSize,
        fromDate,
        toDate,
        investmentAccountId,
      }),
    placeholderData: (data) => data,
    retry: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.results?.length === pageSize
        ? (pages.length ?? 0) + 1
        : undefined;
    },
  });
}

export const portfolioCategorySummaryOptions = (
  variables: {
    investmentAccountId?: string;
    familyId?: number;
  },
  {
    enabled = true,
    initialData,
  }: { enabled?: boolean; initialData?: PortfolioBreakdown[] } = {}
) =>
  queryOptions({
    queryKey: [
      "portfolio-category-summary",
      variables.investmentAccountId ?? null,
      variables.familyId ?? null,
    ],
    queryFn: () => getPortfolioCategorySummary(variables),
    enabled:
      enabled && Boolean(variables.investmentAccountId || variables.familyId),
    initialData,
  });

export function usePortfolioCategorySummary(
  variables: {
    investmentAccountId?: string;
    familyId?: number;
  },
  options?: { enabled?: boolean; initialData?: PortfolioBreakdown[] }
) {
  return useQuery(portfolioCategorySummaryOptions(variables, options));
}

export { getSchemeFolioHoldingsData } from "../../api/clients/portfolio";
